import { ContentStrategyTypeLabel } from '../../../constants'
import {
  BrandPlanTactic,
  BrandPlanContentStrategies,
  ContentStrategy,
  ContentStrategyType,
  ContentStrategyDefaults,
  Goal,
  BusinessType,
} from 'types'

/**
 * Converts an array of strategies to the server's content_strategies format:
 * { [strategy_id]: { seo_contents, email_contents, instagram_contents, crosspost_contents, video_crosspost_contents, length, tactics } }
 */
export const strategiesToContentStrategies = (strategies: Strategy[]): BrandPlanContentStrategies => {
  return strategies.reduce<BrandPlanContentStrategies>((acc, strategy) => {
    acc[strategy.id as ContentStrategyType] = {
      seo_contents: strategy.seo_contents,
      email_contents: strategy.email_contents,
      instagram_contents: strategy.instagram_contents,
      crosspost_contents: strategy.crosspost_contents,
      video_crosspost_contents: strategy.video_crosspost_contents,
      length: strategy.length,
      tactics: strategy.tactics,
    }
    return acc
  }, {})
}

export const buildContentStrategyTactics = (strategy: Strategy): BrandPlanTactic[] => {
  return strategy.tactics.filter((tactic) => {
    return (
      (strategy.crosspost_contents[0].count > 0 ? true : !SOCIAL_TACTICS.includes(tactic)) &&
      (strategy.video_crosspost_contents[0].count > 0 ? true : !VIDEO_TACTICS.includes(tactic)) &&
      (strategy.seo_contents[0].count > 0 ? true : ![BrandPlanTactic.SEO].includes(tactic)) &&
      (strategy.email_contents[0].count > 0 ? true : ![BrandPlanTactic.Email].includes(tactic))
    )
  })
}

export interface Strategy extends ContentStrategy {
  id: string
  name: string
  description: string
  selected_goals: Goal[]
  isSelected?: boolean
}

export const SOCIAL_TACTICS = [
  BrandPlanTactic.Instagram,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Twitter,
]
export const VIDEO_TACTICS = [BrandPlanTactic.TikTok, BrandPlanTactic.YouTube, BrandPlanTactic.InstagramReel]

// Source: https://docs.google.com/spreadsheets/d/1G2-_jRocqV49xeIuigcvT93QdVoFL3pBTtl68RfODdA/edit?gid=1230899626#gid=1230899626
export const strategiesDefaultData: Record<BusinessType, { id: string; name: string; strategies: Strategy[] }> = {
  service: {
    id: 'service',
    name: 'Service-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.ThoughtLeadership,
        name: ContentStrategyTypeLabel[ContentStrategyType.ThoughtLeadership],
        description:
          'Share original perspectives, industry insights, and expert takes on trends shaping your field. Best for building credibility and attracting high-intent prospects.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndServices,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndServices],
        description:
          'Highlight opportunities for clients to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [{ type: 'post', count: 5 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 2 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['convert_clients'],
        length: 1,
      },
      {
        id: ContentStrategyType.EducationalContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.EducationalContent],
        description:
          'Teach something useful about the broader category or problem space, not just your products. Best for reaching new audiences through value.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['generate_leads'],
        length: 1,
      },
      {
        id: ContentStrategyType.QuickTips,
        name: ContentStrategyTypeLabel[ContentStrategyType.QuickTips],
        description:
          'Offer focused, immediately applicable advice your audience can act on today. Best for demonstrating expertise and earning consistent engagement.',
        crosspost_contents: [{ type: 'post', count: 5 }],
        video_crosspost_contents: [{ type: 'post', count: 3 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['drive_repeat_business'],
        length: 1,
      },
    ],
  },
  product: {
    id: 'product',
    name: 'Product-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.LifestyleContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.LifestyleContent],
        description:
          "Show your products in the context of your customer's aspirations and daily life. Best for helping customers picture themselves using the product.",
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['build_product_awareness'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndPromotion,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndPromotion],
        description:
          'Highlight opportunities for customers to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [{ type: 'post', count: 5 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 2 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['make_more_sales'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProductEducation,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProductEducation],
        description:
          'Break down what makes your product/service great: the features, materials, design choices, and craft behind it. Best for turning curious browsers into informed buyers.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['create_repeat_customers'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProblemSolution],
        description:
          'Show the specific problem your product solves in relatable, concrete terms. Best for moving shoppers from curiosity to consideration.',
        crosspost_contents: [{ type: 'post', count: 2 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience'],
        length: 1,
      },
    ],
  },
  place: {
    id: 'place',
    name: 'Place-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.LifestyleContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.LifestyleContent],
        description:
          "Show your products in the context of your customer's aspirations and daily life. Best for helping customers picture themselves using the product.",
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_through_word_of_mouth'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndPromotion,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndPromotion],
        description:
          'Highlight opportunities for customers to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [{ type: 'post', count: 5 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 2 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['bring_in_new_customers'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProductEducation,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProductEducation],
        description:
          'Break down what makes your product/service great: the features, materials, design choices, and craft behind it. Best for turning curious browsers into informed buyers.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['keep_customers_coming_back'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProblemSolution],
        description:
          'Show the specific problem you solve for customers in relatable, concrete terms. Best for moving people from awareness to action.',
        crosspost_contents: [{ type: 'post', count: 2 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['get_found_locally'],
        length: 1,
      },
    ],
  },
}

/**
 * Merges API-provided content count overrides into the base strategiesDefaultData.
 * Only overrides crosspost_contents, video_crosspost_contents, email_contents, seo_contents counts.
 */
export const mergeStrategyDefaults = (
  baseData: typeof strategiesDefaultData,
  overrides: ContentStrategyDefaults | undefined,
): typeof strategiesDefaultData => {
  if (!overrides) return baseData

  const result = { ...baseData }
  for (const businessType of Object.keys(result) as BusinessType[]) {
    const businessOverrides = overrides[businessType]
    if (!businessOverrides) continue

    result[businessType] = {
      ...result[businessType],
      strategies: result[businessType].strategies.map((strategy) => {
        const strategyOverride = businessOverrides[strategy.id]
        if (!strategyOverride) return strategy

        return {
          ...strategy,
          crosspost_contents: strategyOverride.crosspost_contents ?? strategy.crosspost_contents,
          video_crosspost_contents: strategyOverride.video_crosspost_contents ?? strategy.video_crosspost_contents,
          email_contents: strategyOverride.email_contents ?? strategy.email_contents,
          seo_contents: strategyOverride.seo_contents ?? strategy.seo_contents,
        }
      }),
    }
  }
  return result
}

export const numberWithCommas = (number: number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
